/**
 * API Client for Cloudflare Worker functions
 */

export interface User {
    login: string
    name: string | null
    avatar_url: string
}

export interface DocNode {
    name: string
    path: string
    type: 'file' | 'dir'
    title?: string
    description?: string
    icon?: string
    order?: number
    primary?: boolean
    children?: DocNode[]
}

export interface DocSection {
    id: string
    title: string
    description?: string
    icon?: string
    folders: DocNode[]
}

export interface DocTreeResponse {
    title: string
    description?: string
    sections: DocSection[]
}

export interface DocContent {
    path: string
    name: string
    content: string
    sha: string
}

export interface Task {
    id: string
    title: string
    description?: string
    docPath: string
    docSection?: string
    status: 'todo' | 'in_progress' | 'done'
    priority: 'critical' | 'high' | 'medium' | 'low'
    assignee?: string
    labels: string[]
    createdBy: string
    createdAt: string
    updatedBy: string
    updatedAt: string
}

const API_BASE = '/api'

async function request<T>(path: string, options?: RequestInit): Promise<T> {
    const response = await fetch(`${API_BASE}${path}`, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...options?.headers,
        },
    })

    const data = await response.json()

    if (!response.ok) {
        throw new Error(data.error || `Request failed: ${response.status}`)
    }

    return data
}

// Auth
export async function getSession(): Promise<{ authenticated: boolean; user?: User }> {
    return request('/auth/session')
}

export function login() {
    window.location.href = `${API_BASE}/auth/login`
}

export async function logout(): Promise<void> {
    await request('/auth/session', { method: 'DELETE' })
    window.location.reload()
}

// Docs
export async function getDocTree(): Promise<DocTreeResponse> {
    const data = await request<DocTreeResponse | DocNode[] | { error: string }>('/docs/tree')
    if ('error' in data) throw new Error(data.error)

    // Handle backward compatibility - if response is an array, convert to new format
    if (Array.isArray(data)) {
        return {
            title: 'Documentation',
            sections: [{
                id: 'all',
                title: 'All Documents',
                icon: '📚',
                folders: data
            }]
        }
    }

    // Ensure sections is always an array
    if (!data.sections || !Array.isArray(data.sections)) {
        return {
            title: data.title || 'Documentation',
            description: data.description,
            sections: []
        }
    }

    return data
}

export async function getDoc(path: string): Promise<DocContent> {
    const data = await request<{ path: string; name: string; content: string; sha: string } | { error: string }>(`/docs/${path}`)
    if ('error' in data) throw new Error(data.error)
    return data
}

// Tasks
export async function getTasks(filters?: {
    status?: string
    docPath?: string
    assignee?: string
}): Promise<Task[]> {
    const params = new URLSearchParams()
    if (filters?.status) params.set('status', filters.status)
    if (filters?.docPath) params.set('docPath', filters.docPath)
    if (filters?.assignee) params.set('assignee', filters.assignee)

    const query = params.toString() ? `?${params.toString()}` : ''
    const data = await request<{ tasks: Task[] }>(`/tasks${query}`)
    return data.tasks
}

export async function createTask(task: Partial<Task>): Promise<Task> {
    const data = await request<{ task: Task }>('/tasks', {
        method: 'POST',
        body: JSON.stringify(task),
    })
    return data.task
}

export async function updateTask(id: string, updates: Partial<Task>): Promise<Task> {
    const data = await request<{ task: Task }>(`/tasks/${id}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
    })
    return data.task
}

export async function deleteTask(id: string): Promise<void> {
    await request(`/tasks/${id}`, { method: 'DELETE' })
}

// Markdown Tasks
export interface MarkdownTask {
    id: string
    title: string
    description?: string
    status: 'todo' | 'in_progress' | 'done'
    docPath: string
    project: string
    phase?: string
    actor?: string
    section: string
    line: number
    githubUrl?: string
}

export interface TaskFileStats {
    path: string
    name: string
    section: string
    total: number
    completed: number
    inProgress: number
    tasks: MarkdownTask[]
}

export interface MarkdownTasksResponse {
    files: TaskFileStats[]
    tasks: MarkdownTask[]
    totals: { total: number; completed: number; inProgress: number }
}

export async function getMarkdownTasks(): Promise<MarkdownTasksResponse> {
    return request('/tasks/markdown-tasks')
}

// Task Status Overrides (KV persistence)
export interface TaskStatusOverride {
    taskId: string
    status: 'todo' | 'in_progress' | 'done'
    assignee?: string
    updatedBy: string
    updatedAt: string
}

export async function getTaskStatuses(): Promise<{ statuses: Record<string, TaskStatusOverride> }> {
    return request('/tasks/status')
}

export async function updateTaskStatus(
    taskId: string,
    updates: { status?: 'todo' | 'in_progress' | 'done'; assignee?: string }
): Promise<{ success: boolean; status: TaskStatusOverride }> {
    return request('/tasks/status', {
        method: 'PUT',
        body: JSON.stringify({ taskId, ...updates }),
    })
}

// MCP Store - Stored Documents
export interface StoredDocument {
    path: string
    title: string
    content?: string
    tags: string[]
    source: string
    size: number
    createdAt: string
    updatedAt: string
}

export interface StoredDocsListResponse {
    total: number
    returned: number
    documents: StoredDocument[]
}

async function mcpRequest<T>(method: string, args?: Record<string, unknown>): Promise<T> {
    const response = await fetch('/api/mcp-store', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            jsonrpc: '2.0',
            id: Date.now(),
            method: 'tools/call',
            params: { name: method, arguments: args || {} },
        }),
    })

    const data = await response.json()

    if (data.error) {
        throw new Error(data.error.message || 'MCP request failed')
    }

    // Parse the result content
    const content = data.result?.content?.[0]?.text
    if (!content) {
        throw new Error('Empty response from MCP store')
    }

    return JSON.parse(content)
}

export async function listStoredDocs(filters?: {
    prefix?: string
    source?: string
    limit?: number
}): Promise<StoredDocsListResponse> {
    return mcpRequest('list_stored_docs', filters)
}

export async function getStoredDoc(path: string): Promise<StoredDocument> {
    return mcpRequest('get_stored_doc', { path })
}

export async function deleteStoredDoc(path: string): Promise<{ success: boolean; message: string }> {
    return mcpRequest('delete_stored_doc', { path })
}
